/**
 * RoadPositioningHub — third trigger-learning hub. Sister page to
 * JunctionsHub.jsx and RoundaboutsHub.jsx.
 *
 * Teaches PROACTIVE road reading — what to look for, why
 * positioning changes, how far ahead to look. One understanding
 * point at a time, 7-day snooze, badge progression.
 *
 * Feb 12, 2026.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Eye, BookOpen,
  Lightbulb, Target, Sparkles, Award, Lock,
} from "lucide-react";
import RoadPositioningDiagram from "../components/learner/RoadPositioningDiagram";
import CalmEmptyState from "../components/learner/CalmEmptyState";
import RelatedHubsFooter from "../components/learner/RelatedHubsFooter";

const API = process.env.REACT_APP_BACKEND_URL;


export default function RoadPositioningHub() {
  const nav = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [cards, setCards] = useState([]);
  const [badges, setBadges] = useState([]);
  const [viewedCount, setViewedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openCardId, setOpenCardId] = useState(null);
  const carouselRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [sRes, cRes, bRes] = await Promise.all([
          axios.get(`${API}/api/road-positioning/scenarios`),
          axios.get(`${API}/api/road-positioning/cards`),
          axios.get(`${API}/api/road-positioning/me/badges`,
            { withCredentials: true }).catch(() =>
              ({ data: { badges: [], next: [], viewed_count: 0 } })),
        ]);
        setScenarios(sRes.data?.scenarios || []);
        setCards(cRes.data?.cards || []);
        setViewedCount(bRes.data?.viewed_count || 0);
        const locked = (bRes.data?.next || []).map((x) => ({
          ...x, earned: false,
        }));
        const earned = (bRes.data?.badges || []).map((x) => ({
          id: x.badge_id, label: x.label, tagline: x.tagline, earned: true,
        }));
        setBadges([...earned, ...locked]);
      } catch (e) {
        toast.error("Couldn't load Road Reading Hub. Try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function markScenarioViewed(scenarioId) {
    try {
      const { data } = await axios.post(
        `${API}/api/road-positioning/me/scenario-viewed`,
        { scenario_id: scenarioId },
        { withCredentials: true },
      );
      setViewedCount(data?.viewed_count || 0);
      (data?.newly_earned || []).forEach((b) => {
        toast.success(`Badge earned: ${b.label}`, { description: b.tagline });
      });
    } catch (_) { /* silent */ }
  }

  const scrollCarousel = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelector("[data-carousel-item]");
    const cardW = card?.offsetWidth || 280;
    el.scrollBy({ left: dir * (cardW + 12), behavior: "smooth" });
  };

  return (
    <div
      className="bg-slate-50 dark:bg-slate-950 pb-12"
      style={{ minHeight: "100dvh" }}
      data-testid="road-positioning-hub-page"
    >
      <header className="sticky top-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="rp-back"
            onClick={() => nav(-1)}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-600 dark:text-emerald-400">
              Trigger Learning
            </p>
            <h1 className="text-lg font-extrabold text-slate-900 dark:text-white tracking-tight">
              Road Positioning &amp; Reading
            </h1>
          </div>
          <div
            data-testid="rp-progress-pill"
            className="text-[11px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800/40"
          >
            {viewedCount}/{scenarios.length || 10}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-5 space-y-8">
        <section className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-600 dark:text-emerald-400 mb-1.5">
            Why this hub
          </p>
          <h2 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-1.5">
            The road is constantly giving clues.
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            Proactive drivers read the road; reactive drivers respond to it.
            This hub trains you to <em>see</em> the lane, the markings,
            the parked cars and the vanishing point — and decide before
            the road decides for you.
          </p>
        </section>

        {/* SECTION 1: Swipeable scenario carousel */}
        <section data-testid="rp-scenarios-carousel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white inline-flex items-center gap-1.5">
              <Eye className="w-4 h-4 text-emerald-600" />
              Read the road
            </h3>
            <div className="flex items-center gap-1.5">
              <button
                data-testid="rp-carousel-prev"
                onClick={() => scrollCarousel(-1)}
                className="w-8 h-8 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <ChevronLeft className="w-4 h-4 text-slate-500" />
              </button>
              <button
                data-testid="rp-carousel-next"
                onClick={() => scrollCarousel(1)}
                className="w-8 h-8 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <ChevronRight className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>

          <div
            ref={carouselRef}
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4"
            style={{ scrollbarWidth: "none" }}
          >
            {loading && Array.from({ length: 4 }).map((_, i) => (
              <div key={i} data-carousel-item
                   className="flex-shrink-0 w-64 h-72 rounded-2xl bg-slate-200/50 dark:bg-slate-800/50 animate-pulse" />
            ))}
            {!loading && scenarios.length === 0 && (
              <div className="flex-shrink-0 w-full">
                <CalmEmptyState
                  testid="rp-scenarios-empty"
                  tone="nothing-yet"
                  title="Road scenarios loading soon"
                  body="The visual library is being prepared — check back in a moment."
                />
              </div>
            )}
            {!loading && scenarios.map((s) => (
              <article
                key={s.id}
                data-carousel-item
                data-testid={`rp-scenario-${s.id}`}
                onClick={() => markScenarioViewed(s.id)}
                className="snap-start flex-shrink-0 w-64 sm:w-72 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden cursor-pointer"
              >
                <div className="aspect-square bg-slate-900 p-3">
                  <RoadPositioningDiagram id={s.svg} className="w-full h-full" />
                </div>
                <div className="p-4 space-y-2">
                  <h4 className="text-base font-extrabold text-slate-900 dark:text-white leading-tight">
                    {s.label}
                  </h4>
                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    {s.blurb}
                  </p>
                  <ul className="space-y-1 pt-1">
                    {(s.key_features || []).map((f, i) => (
                      <li key={i} className="text-[11px] text-slate-500 dark:text-slate-400 flex items-start gap-1.5">
                        <span className="mt-1 w-1 h-1 rounded-full bg-emerald-500 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {s.common_mistake && (
                    <div className="mt-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl px-3 py-2">
                      <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-400 mb-0.5">
                        Common mistake
                      </p>
                      <p className="text-[11px] text-amber-900 dark:text-amber-200 leading-snug">
                        {s.common_mistake}
                      </p>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 text-center">
            Swipe through the scenarios · {scenarios.length || 10} road situations
          </p>
        </section>

        {/* Badges */}
        {badges.length > 0 && (
          <section data-testid="rp-badges-row">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white inline-flex items-center gap-1.5 mb-3">
              <Award className="w-4 h-4 text-amber-600" />
              Earned along the way
              <span className="ml-1 text-[10px] text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wide">
                {badges.filter(b => b.earned).length} of {badges.length}
              </span>
            </h3>
            <div className="grid grid-cols-3 gap-2.5">
              {badges.map((b) => (
                <div key={b.id}
                  data-testid={`rp-badge-${b.id}`}
                  className={`rounded-2xl border px-3 py-3 transition-colors ${
                    b.earned
                      ? "border-amber-300 dark:border-amber-700 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-900/10"
                      : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 opacity-70"
                  }`}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    {b.earned
                      ? <Award className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      : <Lock className="w-3.5 h-3.5 text-slate-400" />}
                    <p className="text-[10px] uppercase tracking-widest font-extrabold text-slate-700 dark:text-slate-200">{b.label}</p>
                  </div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-snug">
                    {b.tagline || (b.threshold ? `View ${b.threshold} scenarios` : "")}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SECTION 2: Understanding cards */}
        <section data-testid="rp-understanding-cards">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white inline-flex items-center gap-1.5 mb-3">
            <BookOpen className="w-4 h-4 text-emerald-600" />
            One thing at a time
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">
            Each card teaches one piece of road-reading reasoning. Tap to
            expand, then "Got it" to snooze it for 7 days.
          </p>
          <div className="space-y-2.5">
            {loading && Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 rounded-2xl bg-slate-200/50 dark:bg-slate-800/50 animate-pulse" />
            ))}
            {!loading && cards.length === 0 && (
              <CalmEmptyState
                testid="rp-cards-empty"
                tone="nothing-yet"
                title="Understanding cards coming up"
                body="Once they're seeded, you'll see one teaching point at a time here."
              />
            )}
            {!loading && cards.map((c) => (
              <UnderstandingRow key={c.id} card={c}
                open={openCardId === c.id}
                onToggle={() => setOpenCardId(openCardId === c.id ? null : c.id)} />
            ))}
          </div>
        </section>

        <RelatedHubsFooter currentPath="/road-positioning" />
      </main>
    </div>
  );
}


const TONE_BG = {
  emerald: "bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-900/40 text-emerald-900 dark:text-emerald-200",
  blue:    "bg-blue-50 dark:bg-blue-900/15 border-blue-200 dark:border-blue-900/40 text-blue-900 dark:text-blue-200",
  amber:   "bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-900/40 text-amber-900 dark:text-amber-200",
  indigo:  "bg-indigo-50 dark:bg-indigo-900/15 border-indigo-200 dark:border-indigo-900/40 text-indigo-900 dark:text-indigo-200",
  rose:    "bg-rose-50 dark:bg-rose-900/15 border-rose-200 dark:border-rose-900/40 text-rose-900 dark:text-rose-200",
};

function CardFact({ icon: Icon, label, body, tone = "blue" }) {
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${TONE_BG[tone]}`}>
      <p className="text-[10px] uppercase tracking-widest font-extrabold flex items-center gap-1.5 mb-0.5">
        <Icon className="w-3 h-3" />
        {label}
      </p>
      <p className="text-[12px] leading-snug">{body}</p>
    </div>
  );
}

function UnderstandingRow({ card, open, onToggle }) {
  const [busy, setBusy] = useState(false);
  async function markUnderstood() {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/road-positioning/me/understood`,
        { card_id: card.id },
        { withCredentials: true },
      );
      toast.success("Got it — snoozed for 7 days.");
    } catch {
      toast.error("Couldn't save. Try again.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      data-testid={`rp-understanding-${card.id}`}
      className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
    >
      <button type="button"
        data-testid={`rp-understanding-toggle-${card.id}`}
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50">
        <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
          <Lightbulb className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900 dark:text-white leading-snug">{card.title}</p>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-slate-100 dark:border-slate-800">
          <CardFact icon={Sparkles}     label="Why this matters"        body={card.why}       tone="emerald" />
          <CardFact icon={Eye}          label="What to look for"        body={card.look_for}  tone="blue" />
          <CardFact icon={Target}       label="The cue"                 body={card.cue}       tone="amber" />
          <CardFact icon={ChevronRight} label="What to do"              body={card.action}    tone="indigo" />
          <CardFact icon={Lightbulb}    label="Why it works"            body={card.why_works} tone="emerald" />
          <CardFact icon={Sparkles}     label="What success looks like" body={card.success}   tone="rose" />
          <button
            type="button"
            data-testid={`rp-got-it-${card.id}`}
            onClick={markUnderstood}
            disabled={busy}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:bg-emerald-400">
            {busy ? "Saving…" : "Got it — snooze 7 days"}
          </button>
        </div>
      )}
    </div>
  );
}
